/**
 * useLiveFilterPick — keeps an OPEN add-filter draft's column context live.
 *
 * `VqeFilterChipBuilder`'s add mode captures the picked column's
 * `ResolvedFilterColumn` (column adapter, MetaInfo `jsonContext`, owning table)
 * at pick time. MetaInfo can change while the chip is open — a JSON or loose-FK
 * declaration saved from another surface or by MCP, or the MetaInfo fetch simply
 * landing after the pick — and the chip would keep offering the stale operator
 * set. This re-resolves the pick against the CURRENT snapshot + MetaInfo and
 * swaps in the new context when it differs.
 *
 * The user's draft is left alone: only a still-untouched seed (the very object
 * the previous resolution produced) is replaced by the new seed, so a column
 * that just became JSON opens on the `json` operator, while a value the user
 * already typed survives the refresh.
 */

import { useEffect, type Dispatch, type SetStateAction } from 'react';

import type { FilterGroup } from '../../../core/types/workspaceTypes';
import type { ResolvedFilterColumn } from './useVqeFilterColumns';

export type UseLiveFilterPickArgs = {
  /** Re-derives a picker key against the current snapshot + MetaInfo. */
  readonly resolve: (key: string) => ResolvedFilterColumn | null;
  /** The picker key of a plain column pick; null for none / an aggregate template. */
  readonly pickedKey: string | null;
  readonly picked: ResolvedFilterColumn | null;
  readonly setPicked: (next: ResolvedFilterColumn) => void;
  readonly setDraft: Dispatch<SetStateAction<FilterGroup | null>>;
};

export function useLiveFilterPick(args: UseLiveFilterPickArgs): void {
  const { resolve, pickedKey, picked, setPicked, setDraft } = args;
  useEffect(() => {
    if (!picked || pickedKey === null) return;
    const next = resolve(pickedKey);
    // A column that vanished (DDL) keeps the context it was picked with.
    if (!next || sameResolvedFilterColumn(picked, next)) return;
    setPicked(next);
    setDraft((current) => (current === picked.draft ? next.draft : current));
  }, [resolve, pickedKey, picked, setPicked, setDraft]);
}

/**
 * Whether two resolutions of one pick give the chip the same context. `resolve`
 * re-identifies on unrelated re-renders (a new scope object), so this compares
 * content: the column adapter by value (a re-pulled snapshot rebuilds equal
 * column objects), the `jsonContext` fields by reference — MetaInfo included,
 * which React Query keeps stable until a refetch actually changes the record —
 * and every other field by `===`. `draft` and `quoteSiblingField` are skipped:
 * both are derived from the rest.
 */
export function sameResolvedFilterColumn(a: ResolvedFilterColumn, b: ResolvedFilterColumn): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)] as Array<keyof ResolvedFilterColumn>);
  for (const key of keys) {
    // `draft` is derived from the rest; `quoteSiblingField` is a closure over the
    // same qualifier + engine that `quotedField` already captures, rebuilt on
    // every resolve — comparing it by identity would never match and re-render
    // the open draft forever.
    if (key === 'draft' || key === 'quoteSiblingField') continue;
    if (key === 'column') {
      if (JSON.stringify(a.column) !== JSON.stringify(b.column)) return false;
    } else if (key === 'jsonContext') {
      if (!shallowEqual(a.jsonContext, b.jsonContext)) return false;
    } else if (a[key] !== b[key]) {
      return false;
    }
  }
  return true;
}

function shallowEqual(a: object | undefined, b: object | undefined): boolean {
  if (!a || !b) return a === b;
  const ra = a as Record<string, unknown>;
  const rb = b as Record<string, unknown>;
  const keys = new Set([...Object.keys(ra), ...Object.keys(rb)]);
  for (const key of keys) if (ra[key] !== rb[key]) return false;
  return true;
}
